import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { deepCopy } from 'support/helpers/generic/generic';
import { DocumentType } from 'support/types';
import { Tooltip, TooltipContent, TooltipTrigger } from 'components/Display/Tooltip/Tooltip';
import { Checkbox } from 'components/Form/Checkbox/Checkbox';
import { ListTable } from 'components/ListTable/ListTable';
import { TableHeader, TableItem } from 'components/ListTable/types';

import { DocumentTypeSetting } from '../helpers';

type DocumentSettingsCheckboxesProps = {
  documentTypeSettings: Array<DocumentTypeSetting>;
  setDocumentTypeSettings: (updatedSettings: Array<DocumentTypeSetting>) => void;
  banner?: ReactNode;
};
export function DocumentSettingsCheckboxes({
  documentTypeSettings,
  setDocumentTypeSettings,
  banner,
}: DocumentSettingsCheckboxesProps) {
  const { t } = useTranslation();

  const headers: Array<TableHeader> = [
    { key: 'document', label: t('partners:request.documentTypes') },
    {
      key: 'incoming',
      label: t('partners:request.incoming'),
      alignRight: false,
      tooltip: t('partners:request.incomingTooltip'),
    },
    {
      key: 'outgoing',
      label: t('partners:request.outgoing'),
      alignRight: false,
      tooltip: t('partners:request.outgoingTooltip'),
    },
  ];

  const validDocumentTypes = useMemo(() => {
    return Object.values(DocumentType)
      .filter((docType) => docType !== DocumentType.unknown)
      .map((docType) => documentTypeSettings.find((setting) => setting.documentType === docType))
      .filter((setting): setting is DocumentTypeSetting => setting !== undefined);
  }, [documentTypeSettings]);

  const rowBuilder = (item: DocumentTypeSetting): Array<TableItem> => {
    const currentDocumentIndex = documentTypeSettings.findIndex(
      (setting) => setting.documentType === item.documentType,
    );

    return [
      {
        key: `${item.documentType}-name`,
        element: <span className="text-sm text-gray-500">{t('common:messageTypes.plural.' + item.documentType)}</span>,
      },
      {
        key: `${item.documentType}-incoming`,
        element: (
          <Tooltip>
            <TooltipTrigger>
              <Checkbox
                label=""
                disabled={['notAvailable', 'connected'].includes(item.incoming.currentStatus)}
                checked={item.incoming.selected === true || item.incoming.currentStatus === 'connected'}
                onChange={(e) => {
                  const updatedDocumentTypeSettings: Array<DocumentTypeSetting> = deepCopy(documentTypeSettings);
                  updatedDocumentTypeSettings.splice(currentDocumentIndex, 1, {
                    ...item,
                    incoming: { ...item.incoming, selected: e.target.checked },
                  });
                  setDocumentTypeSettings(updatedDocumentTypeSettings);
                }}
              />
            </TooltipTrigger>
            {item.incoming.currentStatus === 'notAvailable' && (
              <TooltipContent>{t('partners:request.incomingNotAvailableTooltip')}</TooltipContent>
            )}
          </Tooltip>
        ),
        className: 'text-right',
      },
      {
        key: `${item.documentType}-outgoing`,
        element: (
          <Tooltip>
            <TooltipTrigger>
              <Checkbox
                label=""
                disabled={['notAvailable', 'connected'].includes(item.outgoing.currentStatus)}
                checked={item.outgoing.selected === true || item.outgoing.currentStatus === 'connected'}
                onChange={(e) => {
                  const updatedDocumentTypeSettings: Array<DocumentTypeSetting> = deepCopy(documentTypeSettings);
                  updatedDocumentTypeSettings.splice(currentDocumentIndex, 1, {
                    ...item,
                    outgoing: { ...item.outgoing, selected: e.target.checked },
                  });
                  setDocumentTypeSettings(updatedDocumentTypeSettings);
                }}
              />
            </TooltipTrigger>
            {item.outgoing.currentStatus === 'notAvailable' && (
              <TooltipContent>{t('partners:request.outgoingNotAvailableTooltip')}</TooltipContent>
            )}
          </Tooltip>
        ),
        className: 'text-right',
      },
    ];
  };

  return (
    <div>
      <span className="mb-4 block text-base font-semibold leading-6 text-gray-900">
        {t('partners:request.documentTypeRequest.label')}
      </span>
      {banner}
      <ListTable headers={headers} data={validDocumentTypes} isLoading={false} rowBuilder={rowBuilder} />
    </div>
  );
}
